import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Users, Crown } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  selectIsOrgOwner,
  selectInstructorId,
} from "../../authentication/authenticationSlice";
import {
  selectColleagues,
  selectInstructorsError,
} from "../../instructors/instructors-slice";
import {
  updateInstructorStatus,
  updateInstructorOrganizationRole,
} from "../../instructors/slice-extensions/instructors-slice-extension";
import { InstructorStatus } from "../../instructors/enums/InstructorStatus";
import { OrganizationRole } from "../../instructors/enums/OrganizationRole";
import { Instructor } from "../../instructors/models/Instructor";
import { selectOrganization } from "../organization-slice";
import { transferOwnership } from "../slice-extensions/organization-slice-extension";
import { fetchColleagues } from "../../instructors/slice-extensions/instructors-slice-extension";
import ConfirmationModal from "../../../components/ConfirmationModal";

const StatusBadge: React.FC<{ instructor: Instructor }> = ({ instructor }) => {
  const { t } = useTranslation();
  return instructor.isActive ? (
    <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
      {t("organization.roster.statusActive", "Active")}
    </span>
  ) : (
    <span className="rounded-full bg-grey-95 px-2 py-0.5 text-xs font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
      {t("organization.roster.statusInactive", "Inactive")}
    </span>
  );
};

const RoleBadge: React.FC<{ role: OrganizationRole }> = ({ role }) => {
  const { t } = useTranslation();
  return (
    <span className="flex items-center gap-1 rounded-full bg-primary-95 px-2 py-0.5 text-xs font-medium text-primary-30 dark:bg-primary-20 dark:text-primary-95">
      {role === OrganizationRole.Owner && <Crown size={12} />}
      {t(`organization.roles.${role}`, role)}
    </span>
  );
};

/**
 * The organization roster: every colleague with role and status controls.
 * Role changes and ownership transfer are Owner-only; activation is Owner/Admin.
 */
const OrganizationRosterSection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const colleagues = useAppSelector(selectColleagues);
  const organization = useAppSelector(selectOrganization);
  const isOwner = useAppSelector(selectIsOrgOwner);
  const viewerInstructorId = useAppSelector(selectInstructorId);
  const lastError = useAppSelector(selectInstructorsError);
  const [transferTarget, setTransferTarget] = useState<Instructor | null>(null);

  const handleRoleChange = (instructor: Instructor, role: OrganizationRole) => {
    if (role === instructor.organizationRole) return;
    dispatch(
      updateInstructorOrganizationRole({ instructorId: instructor.id, role })
    );
  };

  const handleStatusToggle = (instructor: Instructor) => {
    dispatch(
      updateInstructorStatus({
        instructorId: instructor.id,
        status: instructor.isActive
          ? InstructorStatus.Inactive
          : InstructorStatus.Active,
      })
    );
  };

  const handleTransferConfirm = async () => {
    if (!organization || !transferTarget) return;
    const result = await dispatch(
      transferOwnership({
        organizationId: organization.id,
        name: organization.name,
        newOwnerId: transferTarget.id,
      })
    );
    setTransferTarget(null);
    if (transferOwnership.fulfilled.match(result)) {
      // Roles changed on two rows — reload the roster.
      dispatch(fetchColleagues());
    }
  };

  return (
    <div className="with-border rounded-2xl p-4 lg:p-6">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-95 text-primary-50 dark:bg-primary-30 dark:text-primary-80">
          <Users size={20} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg font-semibold leading-tight">
            {t("organization.roster.title", "Instructors")}
          </h2>
          <p className="text-sm text-grey-50 dark:text-grey-70 mt-0.5">
            {t("organization.roster.subtitle", "Everyone in your organization, including deactivated accounts.")}
          </p>
        </div>
      </div>

      {lastError && (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400">{lastError.message}</p>
      )}

      <ul className="divide-y divide-grey-90 dark:divide-primary-20">
        {colleagues.map((instructor) => {
          const isOwnerRow = instructor.organizationRole === OrganizationRole.Owner;
          const isSelfRow = instructor.id === viewerInstructorId;
          const showRoleDropdown = isOwner && !isOwnerRow;
          const showStatusToggle = !isOwnerRow && !isSelfRow;
          const showTransfer = isOwner && !isOwnerRow && instructor.isActive;
          return (
            <li
              key={instructor.id}
              className="flex flex-col gap-3 py-3 lg:flex-row lg:items-center lg:justify-between"
            >
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-medium text-grey-20 dark:text-white">
                    {instructor.fullName}
                    {isSelfRow ? ` ${t("organization.roster.you", "(you)")}` : ""}
                  </p>
                  <RoleBadge role={instructor.organizationRole} />
                  <StatusBadge instructor={instructor} />
                </div>
                <p className="truncate text-sm text-grey-50 dark:text-grey-70">
                  {instructor.email}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {showRoleDropdown && (
                  <select
                    aria-label={t("organization.roster.roleFor", "Role for {{name}}", { name: instructor.fullName })}
                    className="input py-1.5 text-sm"
                    value={instructor.organizationRole}
                    onChange={(e) =>
                      handleRoleChange(instructor, e.target.value as OrganizationRole)
                    }
                  >
                    <option value={OrganizationRole.Admin}>
                      {t("organization.roles.Admin", "Admin")}
                    </option>
                    <option value={OrganizationRole.Instructor}>
                      {t("organization.roles.Instructor", "Instructor")}
                    </option>
                  </select>
                )}
                {showStatusToggle && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => handleStatusToggle(instructor)}
                  >
                    {instructor.isActive
                      ? t("organization.roster.deactivate", "Deactivate")
                      : t("organization.roster.reactivate", "Reactivate")}
                  </button>
                )}
                {showTransfer && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setTransferTarget(instructor)}
                  >
                    <Crown size={18} strokeWidth={2} />
                    <span>{t("organization.roster.transferOwnership", "Transfer ownership")}</span>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <ConfirmationModal
        isOpen={transferTarget !== null}
        title={t("organization.roster.transferOwnership", "Transfer ownership")}
        message={t("organization.roster.transferMessage", "Make {{name}} the owner of {{organization}}? You will become an Admin and only the new owner can transfer ownership back.", {
          name: transferTarget?.fullName ?? "",
          organization:
            organization?.name ?? t("organization.roster.fallbackOrganization", "this organization"),
        })}
        confirmText={t("organization.roster.transferOwnership", "Transfer ownership")}
        cancelText={t("common.cancel", "Cancel")}
        variant="danger"
        onConfirm={handleTransferConfirm}
        onCancel={() => setTransferTarget(null)}
      />
    </div>
  );
};

export default OrganizationRosterSection;
